import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  AdminService,
  OrgSubscription,
  OrgSubscriptionStatus,
  OrgSubscriptionTier,
  PatchedOrgSubscription,
} from '../../core/services/admin.service';
import { OrgRefreshService } from '../../core/services/org-refresh.service';

@Component({
  selector: 'app-subscription-management',
  templateUrl: './subscription-management.component.html',
})
export class SubscriptionManagementComponent implements OnInit, OnDestroy {
  searchQuery = '';
  tierFilter = '';

  subscriptions: OrgSubscription[] = [];
  total = 0;
  nextPageToken: string | null = null;
  loading = true;
  error: string | null = null;

  // True during a silent background refresh (existing rows remain visible)
  backgroundRefreshing = false;

  // Sidebar — open via query param ?org=<orgId> for persistence on reload
  selectedOrgId: string | null = null;

  readonly tierOptions: OrgSubscriptionTier[] = ['FREE', 'PRO', 'ENTERPRISE'];
  readonly statusOptions: OrgSubscriptionStatus[] = ['ACTIVE', 'PAST_DUE', 'CANCELED'];
  readonly SKELETON_ROWS = Array(8).fill(0);

  private refreshSub?: Subscription;

  constructor(
    private readonly adminService: AdminService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly refreshService: OrgRefreshService,
  ) {}

  ngOnInit(): void {
    const orgId = this.route.snapshot.queryParamMap.get('org');
    if (orgId) this.selectedOrgId = orgId;
    this.loadSubscriptions();
    this.refreshSub = this.refreshService.refresh$.subscribe(() => this.softRefresh());
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
  }

  loadSubscriptions(pageToken?: string): void {
    this.loading = true;
    this.error = null;
    this.adminService.listSubscriptions({
      pageToken,
      search: this.searchQuery.trim() || undefined,
      tier:   this.tierFilter || undefined,
    }).subscribe({
      next: res => {
        this.subscriptions = pageToken
          ? [...this.subscriptions, ...res.subscriptions]
          : res.subscriptions;
        this.total         = res.total;
        this.nextPageToken = res.nextPageToken;
        this.loading       = false;
      },
      error: () => {
        this.error   = 'Failed to load subscriptions. Check your connection and try again.';
        this.loading = false;
      },
    });
  }

  // Silent background refresh — keeps current rows visible while fetching.
  // Re-applies the current search/tier filters but resets to page 1.
  // Ignores errors to avoid disrupting the admin while they work.
  softRefresh(): void {
    if (this.loading) return;
    this.backgroundRefreshing = true;
    this.adminService.listSubscriptions({
      search: this.searchQuery.trim() || undefined,
      tier:   this.tierFilter || undefined,
    }).subscribe({
      next: res => {
        this.subscriptions        = res.subscriptions;
        this.total                = res.total;
        this.nextPageToken        = res.nextPageToken;
        this.backgroundRefreshing = false;
      },
      error: () => {
        this.backgroundRefreshing = false;
      },
    });
  }

  applyFilters(): void {
    this.subscriptions = [];
    this.nextPageToken = null;
    this.loadSubscriptions();
  }

  loadMore(): void {
    if (this.nextPageToken) this.loadSubscriptions(this.nextPageToken);
  }

  // ── Sidebar ─────────────────────────────────────────────────────────────────

  openSidebar(orgId: string): void {
    this.selectedOrgId = orgId;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { org: orgId },
      queryParamsHandling: 'merge',
    });
  }

  closeSidebar(): void {
    this.selectedOrgId = null;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { org: null },
      queryParamsHandling: 'merge',
    });
  }

  onSubscriptionSaved(updated: PatchedOrgSubscription): void {
    // Optimistic in-place update so the list reflects the change immediately
    const idx = this.subscriptions.findIndex(s => s.organizationId === updated.organizationId);
    if (idx !== -1) {
      this.subscriptions[idx] = {
        ...this.subscriptions[idx],
        tier:            updated.tier as OrgSubscriptionTier,
        status:          updated.status as OrgSubscriptionStatus,
        nextRenewalDate: updated.nextRenewalDate,
      };
    }
    // Then server-confirm with a background refresh
    this.refreshService.trigger();
  }

  // ── Badge / format helpers (used in the table) ────────────────────────────────

  tierBadgeClass(tier: string): string {
    const map: Record<string, string> = {
      FREE:       'bg-blue-100 text-blue-700',
      PRO:        'bg-violet-100 text-violet-700',
      ENTERPRISE: 'bg-amber-100 text-amber-700',
    };
    return map[tier?.toUpperCase()] ?? 'bg-zinc-100 text-zinc-600';
  }

  statusBadgeClass(status: string): string {
    const map: Record<string, string> = {
      ACTIVE:   'bg-emerald-100 text-emerald-700',
      PAST_DUE: 'bg-red-100 text-red-700',
      CANCELED: 'bg-zinc-100 text-zinc-500',
    };
    return map[status?.toUpperCase()] ?? 'bg-zinc-100 text-zinc-500';
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      ACTIVE:   'Active',
      PAST_DUE: 'Past Due',
      CANCELED: 'Canceled',
    };
    return map[status?.toUpperCase()] ?? status;
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      });
    } catch {
      return '—';
    }
  }

  tierLimit(tier: string): number {
    const defaults: Record<string, number> = { FREE: 1, PRO: 10, ENTERPRISE: 100 };
    return defaults[tier?.toUpperCase()] ?? 0;
  }
}
